import { getAddress } from 'ethers';

/**
 * Canonicalizes an account ID to a single key: strips a `polymarket:` prefix
 * if present (the return value is always bare, never re-prefixed), and
 * checksum-cases EVM addresses.
 *
 * This MUST be total and idempotent — it is the key the trader-selection set is
 * built on, so every representation of one account (bare or prefixed; any letter
 * casing; even a wrong-case paste that fails the EIP-55 checksum) has to collapse
 * to the same string. Otherwise the same trader can be "double selected" when
 * different UI mechanisms supply the address in different forms.
 */
export function toChecksumId(id: string): string {
    const prefix = 'polymarket:';
    let addr = id.startsWith(prefix) ? id.slice(prefix.length) : id;
    // A 40-hex address may arrive in any casing (incl. an invalid-checksum
    // paste). Lowercase it first so getAddress accepts it and returns the
    // canonical checksum regardless of how it was typed.
    if (/^0x[0-9a-fA-F]{40}$/.test(addr)) addr = addr.toLowerCase();
    try {
        return `${getAddress(addr)}`;
    } catch {
        // Non-EVM / proxy id: still normalize prefix + casing so the same
        // account can't appear under two keys.
        return `${addr.toLowerCase()}`;
    }
}
